import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';
import {
  CreativeListRecord,
  CreativeStatusFilter,
  CreativeType,
  isArchivedCreative,
  isOneForAllCreativeType,
  isThirdParty
} from 'core/creative/Creative';
import {
  DefaultCreativeManager,
  CreativeManager
} from 'core/creative/CreativeManager';
import { toast } from 'react-toastify';
import i18n from 'i18next';
import { CreativesWithPagination } from 'ws/CreativeWebService';
import _ from 'lodash';
import { isCompositeSettingBlocked } from 'core/channelGroup/compositeSettingGate';
import { CreativeListDataProvider } from './CreativeListDataProvider';
import { getDataProvider } from './CreativeListDataProviderFactory';
import { Pagination } from 'core/pagination/Pagination';
import { CreativeListCommonColumns } from './CreativeListCommonColumnData';
import { CreativeDetail } from 'components/CreativeDetail/CreativeDetail';
import { DefaultCreativeDetailModel } from 'components/CreativeDetail/CreativeDetailModel';
import { CreativeBindStatusPage } from 'components/CreativeBindStatusPage/CreativeBindStatusPage';
import { AddonFeatureManager } from 'core';
import {
  SessionStorageHelper,
  SessionStorageItemKeys
} from 'helper/StorageHelper';
import {
  SortableList,
  AbstractSortableList
} from 'containers/Common/AbstractSortableList';
import { generateUUID } from 'utils/uuidUtil';
import config from 'config';
import { FilterMenuTabConfig } from 'components/common/FilterMenuTab/FilterMenuTab';

const SIZE_PER_PAGE = 10;

export type CreativeListState = {
  readonly loading: boolean;
  readonly selectedCreatives: number[];
  readonly creativeTypeToShow: number;
  readonly viewModelData: any;
  readonly pagination?: Pagination;
  readonly modalData?: any;
  readonly summaryData: any;
};
export interface CreativeListModel extends SortableList {
  readonly event: UpdateEventListener<CreativeListModel>;
  readonly state: CreativeListState;
  readonly selectedCreatives: (number | undefined)[];
  readonly sizePerPage: number;
  readonly searchString: string;
  readonly filterMenuTabConfigs: FilterMenuTabConfig[];
  readonly noDataIndication: string;
  readonly creativeTypeOptions: {
    label: string;
    value: number;
  }[];
  init(page?: number, newCreativeType?: CreativeType): Promise<void>;
  setSearchbarRef(ref: any): void;
  reviewCreative: (adx, creativeId) => void;
  getColumnsToRender: () => any[];
  updateViewModelData: () => any;
  changeCreativeTypeToRender: (
    creativeType: string | null,
    event?: any
  ) => void;
  changeAdvertiser(advertiserId?: number): Promise<void>;
  sortCreatives(field, direction);
  handleOnSearch(searchString): void;
  handleOnSelect(creativeId: number): void;
  handleOnSelectAll(): void;
  showCreativeDetailModal(creative: CreativeListRecord): void;
  hideModal(): void;
  clearSelectedCreatives(): void;
  updateBindState(enable: boolean, event?: any): Promise<void>;
  cloneCreatives(creatives?: number[]): Promise<void>;
  deleteCreative(creativeIds: number[]): Promise<void>;
  showCreativeBindingStatus(creativeId: number): void;
  showDeleteCreativeModal(creativeIds, event?: any): void;
  toDemoPage(creativeId: number, createTime: string): void;
  canDeleteSelectedCreatives(creativeIds: number[]): boolean;
  isSelectedCreativesProcessing(creativeIds: number[]): boolean;
  onFilterChange: (creativeType: CreativeType, creativeId: string) => void;
  isCreativeTypeSupport: (creativeType: number) => boolean;
  addCache: (key: string, data: any) => void;
  getCache: (key: string) => any;
  getSelectedCreativesCannotUnbind(): number[];
  getSelectedCreativesWithInvalidCompositeSetting(): number[];
  getSelectedOneForAllCreatives(): number[];
}

export type CreativeListProps = {
  readonly model: CreativeListModel;
};

export class DefaultCreativeListModel
  extends AbstractSortableList
  implements CreativeListModel
{
  event: FireableUpdateEventListener<CreativeListModel>;
  creativesWithPagination?: CreativesWithPagination;
  selectedCreatives: number[];
  creativeManager: CreativeManager;
  loading: boolean;
  advertiserId?: number;
  searchString: string;
  statusFilter: CreativeStatusFilter = CreativeStatusFilter.ACTIVE;
  creativeTypeToShow: number;
  listDataProvider: CreativeListDataProvider;
  searchbarRef: any;
  viewModelData: any;
  pagination?: Pagination;
  sortTimeout: any;
  sizePerPage: number;
  modalData: any;
  debouncedOnSearch: any;
  summaryData: any = {};
  onSearchStringChange: (creativeId: string) => void;
  cache: any = {};
  creativeTypeOptions: {
    label: string;
    value: number;
  }[];

  constructor(
    defaultCreativeTypeKey: number | null,
    defaultSearchString: string | null,
    public onFilterChange: (creativeType: number, creativeId: string) => void,
    private addonFeatureManager: AddonFeatureManager,
    creativeManager: CreativeManager = new DefaultCreativeManager()
  ) {
    super(CreativeListCommonColumns.NAME_ID, 'desc');
    this.advertiserId = SessionStorageHelper.getNumberItem(
      SessionStorageItemKeys.ADVERTISER
    );
    this.event = new FireableUpdateEventListener<CreativeListModel>();
    this.selectedCreatives = [];
    this.creativeManager = creativeManager;
    this.loading = true;
    this.searchString = defaultSearchString ? defaultSearchString : '';
    this.creativeTypeOptions = this.creativeManager.getCreativeTypeOptions();
    this.onSearchStringChange = (creativeId: string) => {
      onFilterChange(this.creativeTypeToShow, creativeId);
      this.init(1);
    };
    let firstSupportType = this.creativeTypeOptions.find((option) =>
      this.isCreativeTypeSupport(option.value)
    )?.value;
    if (
      defaultCreativeTypeKey &&
      this.isCreativeTypeSupport(defaultCreativeTypeKey)
    ) {
      firstSupportType = defaultCreativeTypeKey;
    }
    firstSupportType =
      firstSupportType || defaultCreativeTypeKey || CreativeType.IMAGE;
    this.creativeTypeToShow = firstSupportType;
    this.listDataProvider = getDataProvider(this.creativeTypeToShow, this);
    this.sizePerPage = SIZE_PER_PAGE;
    this.debouncedOnSearch = _.debounce(
      this.onSearchStringChange.bind(this),
      1000
    );
    this.updateViewModelData();
  }

  async init(page?: number, newCreativeType?: number) {
    page =
      page !== undefined
        ? page
        : this.creativesWithPagination
          ? this.creativesWithPagination.pagination.page
          : 1;
    this.updateState(true);
    const creativeTypeToShow = newCreativeType
      ? newCreativeType
      : this.creativeTypeToShow;
    try {
      if (isThirdParty(creativeTypeToShow)) {
        await this.creativeManager.getPpsLayoutId();
      }
    } catch (e) {}
    try {
      const pageable = {
        page: page,
        sizePerPage: SIZE_PER_PAGE,
        sort: this.sortField,
        direction: this.sortOrder
      };
      this.creativesWithPagination = await this.creativeManager.getCreatives(
        creativeTypeToShow,
        this.advertiserId,
        pageable,
        this.searchString,
        this.statusFilter
      );
      this.pagination = this.creativesWithPagination
        ? this.creativesWithPagination.pagination
        : undefined;
      this.selectedCreatives = [];
      if (newCreativeType) {
        this.creativeTypeToShow = newCreativeType;
        this.listDataProvider = getDataProvider(newCreativeType, this);
      }
      this.listDataProvider.init && (await this.listDataProvider.init());
      this.updateViewModelData();
    } catch (e) {
      this.updateState(false);
    }
  }

  handleOnSelect = (creativeId) => {
    const creative = this.creativesWithPagination?.creatives.find(
      (c) => c.creativeId === creativeId
    );
    if (creative && isArchivedCreative(creative)) {
      return;
    }
    if (this.selectedCreatives.indexOf(creativeId) > -1) {
      _.remove(this.selectedCreatives, (id) => id === creativeId);
    } else {
      this.selectedCreatives.push(creativeId);
    }
    this.updateViewModelData();
  };

  handleOnSelectAll = () => {
    if (!this.creativesWithPagination) {
      return;
    }

    const selectableCreatives = this.creativesWithPagination.creatives.filter(
      (creative) => !isArchivedCreative(creative)
    );
    if (this.selectedCreatives.length === selectableCreatives.length) {
      this.selectedCreatives = [];
    } else {
      this.selectedCreatives = selectableCreatives.map(
        (creative) => creative.creativeId
      );
    }
    this.updateViewModelData();
  };

  handleOnSearch = (searchString) => {
    this.searchString = searchString;
    if (this.searchString === '') {
      this.debouncedOnSearch && this.debouncedOnSearch.cancel();
      this.onSearchStringChange(searchString);
    } else {
      this.debouncedOnSearch(searchString);
    }
  };

  changeStatusFilter = (status: CreativeStatusFilter) => {
    if (status === this.statusFilter) {
      return;
    }
    this.statusFilter = status;
    this.init(1);
  };

  /**
   * 套用篩選選單的選擇；單選模式下點 tag 的 × 會送空陣列，視為「全部」。
   * @param values 選中的選項文字
   */
  handleOnStatusFilterApply = (values: string[]) => {
    this.changeStatusFilter(this.labelToStatusFilter(_.last(values)));
  };

  private labelToStatusFilter(label?: string): CreativeStatusFilter {
    const labels = this.statusFilterLabels;
    if (label === labels[CreativeStatusFilter.ACTIVE]) {
      return CreativeStatusFilter.ACTIVE;
    }
    if (label === labels[CreativeStatusFilter.ARCHIVED]) {
      return CreativeStatusFilter.ARCHIVED;
    }
    return CreativeStatusFilter.ALL;
  }

  private get statusFilterLabels(): Record<CreativeStatusFilter, string> {
    return {
      [CreativeStatusFilter.ALL]: i18n.t('creativeList.status.all'),
      [CreativeStatusFilter.ACTIVE]: i18n.t('creativeList.status.active'),
      [CreativeStatusFilter.ARCHIVED]: i18n.t('creativeList.status.archived')
    };
  }

  get filterMenuTabConfigs(): FilterMenuTabConfig[] {
    const labels = this.statusFilterLabels;
    return [
      {
        filterType: i18n.t('creativeList.filter.statusFilter'),
        menuTitle: i18n.t('creativeList.filter.statusFilterMenuTitle'),
        tag: i18n.t('creativeList.filter.statusTag'),
        selectedValues: [labels[this.statusFilter]],
        options: [
          labels[CreativeStatusFilter.ALL],
          labels[CreativeStatusFilter.ACTIVE],
          labels[CreativeStatusFilter.ARCHIVED]
        ],
        applyMethod: this.handleOnStatusFilterApply,
        selectionMode: 'single'
      }
    ];
  }

  get noDataIndication(): string {
    const hasCondition =
      this.statusFilter !== CreativeStatusFilter.ALL ||
      this.searchString !== '';
    return hasCondition
      ? i18n.t('creativeList.labels.noMatchData')
      : i18n.t('creativeList.labels.noData');
  }

  canDeleteSelectedCreatives(creativeIds: number[]) {
    if (creativeIds.length === 0) {
      return false;
    }

    const canDeleteCreatives = _.filter(
      this.creativesWithPagination?.creatives,
      (creative) => {
        const selected = creativeIds.indexOf(creative.creativeId) > -1;
        if (!selected) {
          return false;
        }
        return _.get(creative, 'bindingCampaign', []).length === 0;
      }
    );

    return canDeleteCreatives.length === creativeIds.length;
  }

  isSelectedCreativesProcessing(creativeIds: number[]) {
    if (creativeIds.length === 0) {
      return false;
    }

    const hasProcessingCreatives = _.filter(
      this.creativesWithPagination?.creatives,
      (creative) => {
        return creativeIds.indexOf(creative.creativeId) > -1;
      }
    ).some((creative) => {
      return this.creativeManager.isCreativeProcessing(creative);
    });

    return hasProcessingCreatives;
  }

  sortCreatives = (field, direction) => {
    clearTimeout(this.sortTimeout);
    this.sortTimeout = setTimeout(async () => {
      if (this.sortField !== field || this.sortOrder !== direction) {
        this.handleSort(field, direction);
        this.init();
      }
    }, 0);
  };

  reviewCreative = async (adx, creativeId) => {
    this.updateState(true);
    try {
      await this.creativeManager.reviewCreatives([creativeId], [adx]);
      toast.success(i18n.t('creativeList.labels.reviewSuccess'));
      this.updateState(false);
    } catch (e) {
      toast.success(i18n.t('creativeList.labels.reviewFailed'));
      this.updateState();
    }
  };

  get state(): CreativeListState {
    return {
      loading: this.loading,
      selectedCreatives: this.selectedCreatives,
      creativeTypeToShow: this.creativeTypeToShow,
      viewModelData: this.viewModelData,
      summaryData: this.summaryData,
      pagination: this.pagination,
      modalData: this.modalData
    };
  }

  updateViewModelData() {
    if (!this.creativesWithPagination) {
      return (this.viewModelData = []);
    }

    const data = this.creativesWithPagination.creatives.map((creative) => {
      return {
        id: creative.creativeId,
        selected: this.selectedCreatives.indexOf(creative.creativeId) > -1,
        ...this.listDataProvider.getViewModel(creative)
      };
    });

    this.summaryData = this.getSummaryData();
    this.viewModelData = data;
    this.updateState();
  }

  getSummaryData() {
    const creativesCount = this.creativesWithPagination
      ? this.creativesWithPagination.pagination.totalCount
      : 0;
    return {
      id: 0,
      [CreativeListCommonColumns.NAME_ID]: i18n.t(
        'creativeList.labels.creativeCount',
        { count: creativesCount }
      )
    };
  }

  getColumnsToRender() {
    return this.listDataProvider.getColumns();
  }

  changeCreativeTypeToRender = (creativeType: string | null, event?: any) => {
    event && event.stopPropagation();
    if (!creativeType) {
      return;
    }
    if (+creativeType === this.creativeTypeToShow) {
      return;
    }
    this.onFilterChange(+creativeType, this.searchString);
    this.init(1, +creativeType);
  };

  async changeAdvertiser(advertiserId): Promise<void> {
    this.advertiserId = advertiserId;
    if (this.searchbarRef) {
      this.searchbarRef.current.clear();
    } else {
      await this.init(1);
    }
  }

  setSearchbarRef(ref) {
    this.searchbarRef = ref;
  }

  showCreativeDetailModal = (creative: CreativeListRecord) => {
    this.modalData = {
      title: creative.name,
      fullScreen: true,
      component: CreativeDetail,
      componentProps: {
        model: new DefaultCreativeDetailModel(creative)
      }
    };
    this.updateState();
  };

  hideModal = () => {
    this.modalData = undefined;
    this.updateState();
  };

  clearSelectedCreatives = () => {
    this.selectedCreatives = [];
    this.updateViewModelData();
  };

  showDeleteCreativeModal = (creativeIds: number[], event?: any) => {
    event && event.stopPropagation();
    this.modalData = {
      title: i18n.t('creativeList.labels.deleteModalTitle'),
      fullScreen: false,
      message: i18n.t('creativeList.labels.deleteModalContent'),
      confirmBtnData: {
        title: i18n.t('common.buttons.delete'),
        callback: this.deleteCreative.bind(this, creativeIds)
      }
    };
    this.updateState();
  };

  toDemoPage = (creativeId: number, createTime: string) => {
    const uuid = generateUUID(new Date(createTime).getTime());
    const env = config.env === 'live' ? 'prod' : 'beta';
    const link = `https://tenmax-static.cacafly.net/dsp/outdoor_preview/demo.html?creativeId=${creativeId}&uuid=${uuid}&env=${env}`;
    window.open(link, '_blank', 'noopener');
  };

  showCreativeBindingStatus = (creativeId: number) => {
    const close = () => {
      this.modalData = undefined;
    };
    this.modalData = {
      title: i18n.t('creativeBindingStatusPage.title'),
      fullScreen: false,
      component: CreativeBindStatusPage,
      componentProps: {
        creativeId,
        onClose: close
      }
    };
    this.updateState();
  };

  async updateBindState(enable: boolean, event?: any) {
    event && event.stopPropagation();
    this.updateState(true);
    try {
      await this.creativeManager.updateCreativeBindingStatus(
        this.selectedCreatives,
        enable
      );
      await this.init();
      toast.success(i18n.t('creativeList.labels.updateBindStateSuccess'));
    } catch (e) {
      e instanceof Error && toast.error(e.message);
      this.updateState();
    }
  }

  async updateBindStateOfCampaigns(
    creativeId: number,
    campaignIds: number[],
    isActive: boolean
  ) {
    this.updateState(true);
    this.modalData = undefined;
    try {
      await this.creativeManager.updateCreativeBindingCampaigns(
        creativeId,
        campaignIds,
        isActive
      );
      await this.init();
      toast.success(i18n.t('creativeList.labels.updateBindStateSuccess'));
    } catch (e) {
      e instanceof Error && toast.error(e.message);
      this.updateState();
    }
  }

  cloneCreatives = async (creatives: number[] | undefined) => {
    const creativesToClone = creatives ? creatives : this.selectedCreatives;
    creativesToClone.sort((a, b) => a - b);
    this.updateState(true);
    try {
      await this.creativeManager.cloneCreatives(creativesToClone);
      await this.init();
      toast.success(i18n.t('creativeList.labels.cloneCreativeSuccess'));
    } catch (e) {
      e instanceof Error && toast.error(e.message);
      this.updateState(false);
    }
  };

  deleteCreative = async (creativeIds: number[]) => {
    this.updateState(true);
    this.modalData = undefined;
    try {
      await this.creativeManager.deleteCreative(creativeIds);
      await this.init(
        this.creativesWithPagination
          ? this.creativesWithPagination.pagination.page
          : 1
      );
      toast.success(i18n.t('creativeList.labels.deleteCreativeSuccess'));
    } catch (e) {
      e instanceof Error && toast.error(e.message);
      this.updateState(false);
    }
  };

  isCreativeTypeSupport = (creativeType) => {
    return this.creativeManager.isCreativeTypeSupport(
      creativeType,
      this.addonFeatureManager
    );
  };

  addCache = (key: string, data: any) => {
    this.cache[key] = data;
  };

  getCache = (key: string) => {
    return this.cache[key];
  };

  getSelectedCreativesCannotUnbind() {
    if (!this.creativesWithPagination) {
      return [];
    }
    const creatives = this.creativesWithPagination.creatives;
    return this.selectedCreatives.filter((creativeId) => {
      const creative = creatives.find((c) => c.creativeId === creativeId);
      if (!creative) {
        return false;
      }
      if (creative.bindingCampaign.length === 0) {
        return false;
      }
      return creative.bindingCampaign.some((campaign) => !campaign.canUnbind);
    });
  }

  /**
   * 勾選中的素材裡，有哪些是只能唯讀的 One For All 素材。
   * @returns One For All 素材的 ID
   */
  getSelectedOneForAllCreatives() {
    if (!this.creativesWithPagination) {
      return [];
    }
    const creatives = this.creativesWithPagination.creatives;
    return this.selectedCreatives.filter((creativeId) => {
      const creative = creatives.find((c) => c.creativeId === creativeId);
      return (
        !!creative && isOneForAllCreativeType(creative.creativeType as number)
      );
    });
  }

  /**
   * ECOPD-5722：勾選中的素材裡，有哪些所屬渠道的合成設定不可用。
   * 只要有一筆，批次複製就不給按 —— 後端會逐筆擋，讓使用者複製一半再收到錯誤更難理解。
   */
  getSelectedCreativesWithInvalidCompositeSetting() {
    if (!this.creativesWithPagination) {
      return [];
    }
    const creatives = this.creativesWithPagination.creatives;
    return this.selectedCreatives.filter((creativeId) => {
      const creative = creatives.find((c) => c.creativeId === creativeId);
      return (
        !!creative && isCompositeSettingBlocked(creative.creativeType as number)
      );
    });
  }

  updateState(loading = false) {
    this.loading = loading;
    this.event.fireEvent(this);
  }
}
